import React, { useState, useRef, useCallback, useEffect } from 'react';
import API_URL from '../config';
import axios from 'axios';
import './BulkUploadModal.css';
import RoleField from './shared/RoleField';

function haversineMiles(lat1, lon1, lat2, lon2) {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function geocodeCity(cityState) {
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityState)}&format=json&limit=1`,
      { headers: { 'User-Agent': 'MountainRecruitingCRM/1.0' } }
    );
    const data = await resp.json();
    if (data?.[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch {}
  return null;
}

const sleep = ms => new Promise(r => setTimeout(r, ms));

function BulkUploadModal({ companies, csas, onClose, onComplete }) {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCsa, setSelectedCsa] = useState('');
  const [screeningStatus, setScreeningStatus] = useState('');
  const [jobRole, setJobRole] = useState(''); // '' = Driver (October 2026)
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [locationWarning, setLocationWarning] = useState(null);
  const [locationCheckDone, setLocationCheckDone] = useState(false);
  const fileInputRef = useRef(null);
  const locationCheckRan = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!result || result.processed === 0 || locationCheckRan.current) return;
    locationCheckRan.current = true;

    const companyId = selectedCompany; // already resolved from CSA when upload ran
    const processed = result.details?.processed || [];

    (async () => {
      try {
        const token = localStorage.getItem('token');
        const locResp = await axios.get(`${API_URL}/api/companies/${companyId}/location`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const companyLoc = locResp.data;
        if (!companyLoc?.city || !companyLoc?.state) return;

        const companyGeo = await geocodeCity(`${companyLoc.city}, ${companyLoc.state}`);
        if (!companyGeo) return;
        await sleep(1100);

        let farCount = 0;
        let checkedCount = 0;

        for (const candidate of processed) {
          if (!isMounted.current) return;
          if (!candidate.location) continue;
          const match = candidate.location.match(/^([^,]+),\s*([A-Z]{2})/);
          if (!match) continue;
          const geo = await geocodeCity(`${match[1].trim()}, ${match[2]}`);
          if (geo) {
            if (haversineMiles(companyGeo.lat, companyGeo.lng, geo.lat, geo.lng) > 100) farCount++;
            checkedCount++;
          }
          await sleep(1100);
        }

        if (!isMounted.current) return;
        if (checkedCount > 0 && farCount > checkedCount / 2) {
          setLocationWarning({
            farCount,
            checkedCount,
            companyCity: `${companyLoc.city}, ${companyLoc.state}`
          });
        }
      } catch (err) {
        console.error('Location check error:', err);
      } finally {
        if (isMounted.current) setLocationCheckDone(true);
      }
    })();
  }, [result, selectedCompany]);

  const validateAndAddFiles = useCallback((newFiles) => {
    const errs = [];
    const valid = [];

    Array.from(newFiles).forEach(file => {
      if (file.type !== 'application/pdf') {
        errs.push(`"${file.name}" is not a PDF — skipped`);
      } else if (files.length + valid.length >= 20) {
        errs.push(`"${file.name}" skipped — max 20 files`);
      } else {
        valid.push(file);
      }
    });

    setErrors(errs);
    setFiles(prev => [...prev, ...valid].slice(0, 20));
  }, [files.length]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndAddFiles(e.dataTransfer.files);
  }, [validateAndAddFiles]);

  const handleFileInput = (e) => {
    validateAndAddFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!selectedCompany && !selectedCsa) {
      setErrors(['Please select a company before uploading.']);
      return;
    }
    if (!screeningStatus) {
      setErrors(['Please select a screening status before uploading.']);
      return;
    }
    if (files.length === 0) {
      setErrors(['Please add at least one PDF file.']);
      return;
    }

    setErrors([]);
    setUploading(true);
    setProgress(`Processing ${files.length} resume${files.length > 1 ? 's' : ''}... This may take a minute.`);

    const formData = new FormData();
    if (selectedCsa) formData.append('csa_id', selectedCsa);
    if (selectedCompany) formData.append('company_id', selectedCompany);
    formData.append('screening_status', screeningStatus);
    formData.append('job_role', jobRole);
    files.forEach(f => formData.append('resumes', f));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/candidates/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000 // 5 min timeout for large batches
      });

      setResult(response.data);
      setProgress('');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      setErrors([msg]);
      setProgress('');
    } finally {
      setUploading(false);
    }
  };

  const handleDone = () => {
    onComplete();
    onClose();
  };

  if (result) {
    return (
      <div className="bulk-modal-overlay" onClick={onClose}>
        <div className="bulk-modal" onClick={e => e.stopPropagation()}>
          {locationWarning && (
            <div className="bulk-location-warning-overlay">
              <div className="bulk-location-warning-box">
                <div className="bulk-location-warning-icon">⚠️</div>
                <h3>Wrong Company Selected?</h3>
                <p>
                  <strong>{locationWarning.farCount} of {locationWarning.checkedCount}</strong> candidate{locationWarning.checkedCount !== 1 ? 's' : ''} appear
                  to be more than 100 miles from <strong>{locationWarning.companyCity}</strong>.
                </p>
                <p>Please verify you selected the correct company before continuing.</p>
                <button className="add-btn" onClick={() => setLocationWarning(null)}>OK, Continue</button>
              </div>
            </div>
          )}
          <div className="bulk-modal-header">
            <h2>Upload Complete</h2>
            <button className="bulk-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="bulk-result">
            <div className="bulk-result-stats">
              <div className="bulk-stat success">
                <span className="bulk-stat-num">{result.processed}</span>
                <span className="bulk-stat-label">Added</span>
              </div>
              <div className="bulk-stat duplicate">
                <span className="bulk-stat-num">{result.duplicates}</span>
                <span className="bulk-stat-label">Duplicates Skipped</span>
              </div>
              <div className="bulk-stat failed">
                <span className="bulk-stat-num">{result.failed}</span>
                <span className="bulk-stat-label">Failed</span>
              </div>
            </div>

            {result.details?.processed?.length > 0 && (
              <div className="bulk-result-section">
                <h4>✅ Added Candidates</h4>
                {result.details.processed.map((p, i) => (
                  <div key={i} className="bulk-result-item success-item">
                    <span>{p.name}</span>
                    <span className="bulk-result-file">{p.filename}</span>
                  </div>
                ))}
              </div>
            )}

            {result.details?.duplicates?.length > 0 && (
              <div className="bulk-result-section">
                <h4>⚠️ Duplicates Skipped</h4>
                {result.details.duplicates.map((d, i) => (
                  <div key={i} className="bulk-result-item dup-item">
                    <span>{d.name}</span>
                    <span className="bulk-result-file">{d.filename}</span>
                  </div>
                ))}
              </div>
            )}

            {result.details?.failed?.length > 0 && (
              <div className="bulk-result-section">
                <h4>❌ Failed</h4>
                {result.details.failed.map((f, i) => (
                  <div key={i} className="bulk-result-item fail-item">
                    <span>{f.filename}</span>
                    <span className="bulk-result-error">{f.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!locationCheckDone && result.processed > 0 && (
            <p className="bulk-location-checking">Verifying candidate locations...</p>
          )}
          <div className="bulk-modal-footer">
            <button className="add-btn" onClick={handleDone}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-modal-overlay" onClick={uploading ? null : onClose}>
      <div className="bulk-modal" onClick={e => e.stopPropagation()}>
        <div className="bulk-modal-header">
          <h2>Bulk Add Candidates</h2>
          {!uploading && <button className="bulk-modal-close" onClick={onClose}>✕</button>}
        </div>

        <div className="bulk-modal-body">
          <div className="bulk-company-select">
            <label>Company <span className="required">*</span></label>
            <select
              value={selectedCompany}
              onChange={e => { setSelectedCompany(e.target.value); setSelectedCsa(''); }}
              disabled={uploading}
            >
              <option value="">Select a company...</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="bulk-company-select">
            <label>CSA <span className="required">*</span></label>
            <select
              value={selectedCsa}
              onChange={e => setSelectedCsa(e.target.value)}
              disabled={uploading}
              required
            >
              <option value="">Select a CSA...</option>
              {(csas || []).filter(c => !selectedCompany || String(c.company_id) === String(selectedCompany)).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="bulk-company-select">
            <label>Screening Status <span className="required">*</span></label>
            <select
              value={screeningStatus}
              onChange={e => setScreeningStatus(e.target.value)}
              disabled={uploading}
            >
              <option value="">Select screening status...</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
              <option value="Not Responding">Not Responding</option>
              <option value="Not Interested">Not Interested</option>
            </select>
          </div>

          <RoleField className="bulk-company-select" label="Role (whole batch)" value={jobRole} onChange={setJobRole} disabled={uploading} />

          <div
            className={`bulk-drop-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'disabled' : ''}`}
            onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={uploading ? e => e.preventDefault() : handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
            <div className="bulk-drop-icon">📄</div>
            <p className="bulk-drop-text">
              {dragOver ? 'Drop PDFs here' : 'Drag & drop resumes here, or click to browse'}
            </p>
            <p className="bulk-drop-hint">PDF files only · Max 20 files per batch</p>
          </div>

          {errors.length > 0 && (
            <div className="bulk-errors">
              {errors.map((e, i) => <p key={i} className="bulk-error">{e}</p>)}
            </div>
          )}

          {files.length > 0 && (
            <div className="bulk-file-list">
              <div className="bulk-file-list-header">
                <span>{files.length} file{files.length > 1 ? 's' : ''} selected</span>
                {!uploading && (
                  <button className="bulk-clear-all" onClick={() => setFiles([])}>Clear all</button>
                )}
              </div>
              {files.map((f, i) => (
                <div key={i} className="bulk-file-item">
                  <span className="bulk-file-icon">📄</span>
                  <span className="bulk-file-name">{f.name}</span>
                  <span className="bulk-file-size">{(f.size / 1024).toFixed(0)} KB</span>
                  {!uploading && (
                    <button className="bulk-file-remove" onClick={() => removeFile(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="bulk-progress">
              <div className="bulk-spinner" />
              <p>{progress}</p>
              <p className="bulk-progress-note">AI is reading and extracting candidate data from each resume...</p>
            </div>
          )}
        </div>

        <div className="bulk-modal-footer">
          {!uploading && (
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
          )}
          <button
            className="add-btn"
            onClick={handleUpload}
            disabled={uploading || files.length === 0 || !selectedCsa || !screeningStatus}
          >
            {uploading ? 'Processing...' : `Upload ${files.length > 0 ? files.length + ' ' : ''}Resume${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkUploadModal;
